"use client";

import clsx from "clsx";
import React, {forwardRef, useRef, useState} from "react";
import {splitSlashCommand} from "@/utils/chat/slashCommand";

interface HighlightedTextareaProps
   extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value"> {
   value: string;
   className?: string;
   wrapperClassName?: string;
   isSlashMenuOpen?: boolean;
   skillSlugs?: readonly string[];
}

// The backdrop colours the token while the transparent textarea retains native editing.
const HighlightedTextarea = forwardRef<HTMLTextAreaElement, HighlightedTextareaProps>(
   ({value, className, wrapperClassName, isSlashMenuOpen = false, skillSlugs = [], onScroll, onKeyDown, onSelect, ...rest}, ref) => {
      const backdropRef = useRef<HTMLDivElement>(null);
      const [tokenSelected, setTokenSelected] = useState(false);
      const candidate = splitSlashCommand(value);
      const split = candidate && skillSlugs.includes(candidate.token.slice(1)) && (!candidate.after || /^\s/.test(candidate.after)) ? candidate : null;
      const tokenStart = split ? split.before.length : 0;
      const tokenEnd = split ? tokenStart + split.token.length : 0;

      const isTokenSelection = (el: HTMLTextAreaElement) =>
         !!split && el.selectionStart === tokenStart && el.selectionEnd === tokenEnd;

      // Select the token on the first delete so the next keypress removes it atomically.
      const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
         const isDelete = e.key === "Backspace" || e.key === "Delete";
         const hasModifier = e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;
         const el = e.currentTarget;
         if (isDelete && !isSlashMenuOpen && !hasModifier && split && el.selectionStart === el.selectionEnd) {
            const caret = el.selectionStart;
            const atTrailingEdge = e.key === "Backspace" && caret === tokenEnd;
            const atLeadingEdge = e.key === "Delete" && caret === tokenStart;
            if (atTrailingEdge || atLeadingEdge) {
               e.preventDefault();
               el.setSelectionRange(tokenStart, tokenEnd);
               setTokenSelected(true);
               return;
            }
         }
         onKeyDown?.(e);
      };

      return (
         <div className={clsx("relative", wrapperClassName)}>
            <div
               ref={backdropRef}
               aria-hidden="true"
               className={clsx(
                  className,
                  "pointer-events-none absolute inset-0 select-none overflow-hidden whitespace-pre-wrap break-words"
               )}
            >
               {split ? (
                  <>
                     {split.before}
                     {/* The negative margin offsets pill padding to keep the overlay aligned. */}
                     {tokenSelected ? (
                        <span className="-mx-1 rounded-md bg-blue-100 px-1 py-0.5 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                           {split.token}
                        </span>
                     ) : (
                        <span className="text-blue-600 dark:text-blue-300">{split.token}</span>
                     )}
                     {split.after}
                  </>
               ) : (
                  value
               )}
               {/* Preserve the final line without adding a second blank line. */}
               {value.endsWith("\n") ? "\u200b" : "\n"}
            </div>
            <textarea
               ref={ref}
               value={value}
               onKeyDown={handleKeyDown}
               onSelect={(e) => {
                  setTokenSelected(isTokenSelection(e.currentTarget));
                  onSelect?.(e);
               }}
               onScroll={(e) => {
                  if (backdropRef.current) {
                     backdropRef.current.scrollTop = e.currentTarget.scrollTop;
                  }
                  onScroll?.(e);
               }}
               className={clsx(
                  className,
                  "relative bg-transparent",
                  split && tokenSelected && "selection:bg-transparent selection:text-transparent"
               )}
               // Let the backdrop provide text color without hiding the native caret.
               style={{color: "transparent", caretColor: "rgb(var(--1000-color))"}}
               {...rest}
            />
         </div>
      );
   }
);

HighlightedTextarea.displayName = "HighlightedTextarea";

export default HighlightedTextarea;
